import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
} from "@mui/material";
import { Email } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import logo from "../../assets/MariTest_logomark.png";
import dashboardPreview from "../../assets/dashboard-preview.png";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Use same API base as AuthContext — RELATIVE path (required for Vercel rewrites)
  const API_BASE = "";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email.trim()) return setError("Please enter your email address.");
    if (!/\S+@\S+\.\S+/.test(email))
      return setError("Please enter a valid email address.");

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setMessage("A password reset link has been sent to your email.");
      } else {
        setError(data.message || `Request failed with status ${res.status}`);
      }
    } catch (err) {
      console.error("Forgot-password error:", err);
      setError("Unable to reach the server. Please try again.");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        bgcolor: "#0b1120",
        position: "fixed",
        inset: 0,
      }}
    >
      {/* Left Section */}
      <Box
        sx={{
          flex: { xs: 1, md: "0 0 45%" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 3, sm: 6, md: 8 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box sx={{ mb: 6, textAlign: "center" }}>
            <img
              src={logo}
              alt="MariTest Logo"
              style={{
                width: 100,
                marginBottom: "1.5rem",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />

            <Typography variant="h5" sx={{ fontWeight: 700, color: "#f9fafb" }}>
              Forgot Password
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: "#d1d5db", mt: 1, fontSize: "0.95rem" }}
            >
              Enter your email and we’ll send you a reset link.
            </Typography>
          </Box>

          {message && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                backgroundColor: "rgba(16,185,129,0.1)",
                color: "#4ade80",
              }}
            >
              {message}
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: "rgba(239,68,68,0.1)",
                color: "#f87171",
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              sx={{
                mb: 3,
                input: { color: "#f3f4f6" },
                label: { color: "#9ca3af" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#111827",
                  "& fieldset": { borderColor: "#1f2937" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                backgroundColor: "#3b82f6",
                py: 1.3,
                fontWeight: 600,
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 15px rgba(59,130,246,0.25)",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>

            <Typography
              onClick={() => navigate("/login")}
              sx={{
                mt: 3,
                textAlign: "center",
                color: "#60a5fa",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { color: "#93c5fd" },
              }}
            >
              Back to Login
            </Typography>
          </form>
        </Box>
      </Box>

      {/* Right Background of */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          component="img"
          src={dashboardPreview}
          alt="Dashboard Background"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "blur(6px) brightness(0.35)",
          }}
        />
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
